/**
 * useDynamicOptions Hook
 *
 * Fetches dynamic dropdown options for parameters with x-ms-dynamic-values configuration.
 * Extracts the fetching logic from DynamicDropdownField to enable reuse with standard
 * field components (ComboboxField, etc.).
 *
 * Usage:
 * ```tsx
 * const { options, isLoading, error, refetch } = useDynamicOptions({
 *   parameterInfo,
 *   parameterValues,
 * });
 *
 * return <ComboboxField options={options} isLoading={isLoading} ... />;
 * ```
 */

import { useMemo } from 'react';
import { useConnectorDynamicList } from './useDynamicParameters';
import { useClientDynamicList } from './useClientDynamicList';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { useConnectionStore } from '@/stores/connectionStore';
import type { ParameterInfo } from '@/types/operationParameter';
import { isValueSegmentArray, segmentsToString } from '@/types/valueSegment';

export interface DynamicOption {
  label: string;
  value: string;
  description?: string;
  /** Raw source item from the resolver, consumed by x-ms-on-select mappings. */
  data?: Record<string, unknown>;
}

/**
 * Locate the active connection reference for the current node, walking
 * multiple storage locations in priority order:
 *
 *   1. `connectionMapping[nodeId]` — standard managed-API connections.
 *   2. `parameterValues._connection` — built-in HTTP connections written
 *      directly into the parameter bag.
 *   3. `parameterValues._modelConnection_{slot}` — agent slot selection
 *      made by ConnectionPicker but not yet persisted to nodeData.
 *   4. `nodeInputs.modelConfigurations.{slot}.referenceName` — persisted
 *      agent slot, loaded from a saved workflow.
 *
 * Returns the first key that resolves to a known connection reference, or
 * undefined if the node has no usable connection yet.
 */
function resolveConnectionReferenceKey({
  nodeId,
  nodeInputs,
  parameterValues,
  connectionMapping,
  connectionReferences,
}: {
  nodeId: string;
  nodeInputs: Record<string, unknown> | undefined;
  parameterValues: Record<string, unknown>;
  connectionMapping: Record<string, string | null | undefined>;
  connectionReferences: Record<string, unknown>;
}): string | undefined {
  const isKnown = (key: unknown): key is string =>
    typeof key === 'string' && key.length > 0 && key in connectionReferences;

  const direct = connectionMapping[nodeId];
  if (isKnown(direct)) return direct;

  const fromConnectionParam = parameterValues['_connection'];
  if (isKnown(fromConnectionParam)) return fromConnectionParam;

  for (const [key, val] of Object.entries(parameterValues)) {
    if (key.startsWith('_modelConnection_') && isKnown(val)) return val;
  }

  const modelConfigurations = (nodeInputs as
    | { modelConfigurations?: Record<string, { referenceName?: string }> }
    | undefined)?.modelConfigurations;
  if (modelConfigurations) {
    for (const config of Object.values(modelConfigurations)) {
      if (isKnown(config?.referenceName)) return config.referenceName;
    }
  }

  return undefined;
}

export interface UseDynamicOptionsParams {
  /** The parameter info containing dynamicValues configuration */
  parameterInfo: ParameterInfo | undefined;
  /** Current parameter values (for dependency resolution) */
  parameterValues?: Record<string, unknown>;
  /** Whether to auto-fetch when dependencies are satisfied (default: true) */
  autoFetch?: boolean;
  /** Static fallback options if dynamic loading fails */
  staticOptions?: DynamicOption[];
}

export interface UseDynamicOptionsResult {
  /** The fetched options (or static fallback) */
  options: DynamicOption[];
  /** Whether options are currently loading */
  isLoading: boolean;
  /** Error message if loading failed */
  error: string | undefined;
  /** True when the error is auth-related and an interactive re-auth could resolve it. */
  isAuthError: boolean;
  /**
   * Whether a fetch has completed successfully, even when it returned zero
   * options. When true, the (possibly empty) dynamic result is authoritative
   * and should be shown instead of static fallback options.
   */
  hasLoaded: boolean;
  /** Whether all dependencies are satisfied */
  dependenciesSatisfied: boolean;
  /** Names of missing dependencies */
  missingDependencies: string[];
  /**
   * True when the resolved connection reference is absent or incomplete
   * (template placeholder shell, no connection.id / connectionProperties, etc.).
   * The underlying fetch is skipped in this state; consumers can render a
   * “connect first” affordance.
   */
  missingConnection: boolean;
  /** Dependencies bound to runtime expressions; options can't be fetched. */
  runtimeDependencies: string[];
  /** Refetch the options */
  refetch: () => void;
}

/**
 * Hook to fetch dynamic options for a parameter
 */
export function useDynamicOptions({
  parameterInfo,
  parameterValues: externalParamValues,
  autoFetch = true,
  staticOptions = [],
}: UseDynamicOptionsParams): UseDynamicOptionsResult {
  // Get node context from store if not provided
  const node = useNodeEditorStore((state) => state.node);
  const storeParameterValues = useNodeEditorStore((state) => state.parameterValues);
  const parameterDefinitions = useNodeEditorStore((state) => state.parameterDefinitions);
  const nodeId = node?.id ?? '';

  // Use external param values if provided, otherwise use store values
  const rawParameterValues = externalParamValues ?? storeParameterValues;

  // Convert parameter values from ValueSegment[] to plain values for dependency resolution
  const parameterValues = useMemo((): Record<string, unknown> => {
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(rawParameterValues)) {
      if (isValueSegmentArray(val)) {
        result[key] = segmentsToString(val);
      } else {
        result[key] = val;
      }
    }
    return result;
  }, [rawParameterValues]);

  // The client path resolves to typed values: flattening everything to a string
  // would destroy the JSON type a body assignment has to preserve. Only an empty
  // segment list collapses, and it collapses to undefined so an unset field reads
  // as missing rather than as an empty string.
  const typedParameterValues = useMemo((): Record<string, unknown> => {
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(rawParameterValues)) {
      if (isValueSegmentArray(val)) {
        const text = segmentsToString(val);
        if (text !== '') result[key] = text;
      } else if (val !== undefined) {
        result[key] = val;
      }
    }
    return result;
  }, [rawParameterValues]);

  const clientDynamicConfig = parameterInfo?.info?.clientDynamicValues;

  const manifestInputNames = useMemo(
    () => new Set((parameterDefinitions ?? []).map((definition) => definition.parameterName)),
    [parameterDefinitions]
  );

  // Get connection context
  const connectionMapping = useConnectionStore((state) => state.mapping);
  const connectionReferences = useConnectionStore((state) => state.references);

  const connectionReferenceKey = resolveConnectionReferenceKey({
    nodeId,
    nodeInputs: (node?.nodeData as { inputs?: Record<string, unknown> } | undefined)?.inputs,
    parameterValues,
    connectionMapping,
    connectionReferences,
  });
  const connectionReference = connectionReferenceKey
    ? connectionReferences[connectionReferenceKey]
    : undefined;

  // Extract connector ID from connection reference or node data
  const connectorId = useMemo(() => {
    const idFromData = connectionReference?.api?.id ??
      ((node?.nodeData as Record<string, unknown>)?.connectorId as string) ??
      '';
      // TODO: Remove this once new connections integration is completed.
    const formedId = idFromData.toLowerCase().includes('/managedapis') ? `/managedapis/${idFromData.split('/').slice(-1)[0]}` : idFromData;
    return formedId;
  }, [connectionReference, node]);

  const dynamicConfig = parameterInfo?.info?.dynamicValues ?? { operationId: '' };

  // Use the connector dynamic list hook
  const {
    options: dynamicOptions,
    isLoading,
    error: dynamicError,
    isAuthError,
    hasLoaded,
    dependenciesSatisfied,
    missingDependencies,
    missingConnection,
    runtimeDependencies,
    refetch,
  } = useConnectorDynamicList({
    dynamicConfig,
    connectionReference,
    connectorId,
    parameterValues,
    autoFetch: autoFetch && !clientDynamicConfig,
    isFocused: autoFetch && !clientDynamicConfig,
  });

  const client = useClientDynamicList({
    config: clientDynamicConfig,
    parameterValues: typedParameterValues,
    manifestInputNames,
    autoFetch,
  });

  // Once a fetch has completed, the dynamic result is authoritative — even when
  // it returned zero options (e.g. a SQL trigger with no eligible tables). Only
  // fall back to static options before the first successful load or on error.
  const options: DynamicOption[] = useMemo(() => {
    const source = clientDynamicConfig ? client.options : dynamicOptions;
    const loaded = clientDynamicConfig ? client.hasLoaded : hasLoaded;

    if (loaded) {
      return source.map((opt) => ({
        value: opt.value,
        label: opt.label,
        ...(opt.description ? { description: opt.description } : {}),
        ...(opt.data ? { data: opt.data } : {}),
      }));
    }
    return staticOptions;
  }, [clientDynamicConfig, client.options, client.hasLoaded, hasLoaded, dynamicOptions, staticOptions]);

  // Combine errors
  const error = useMemo(() => {
    if (clientDynamicConfig) return client.error;
    if (dynamicError && !dynamicError.includes('Waiting for')) return dynamicError;
    return undefined;
  }, [clientDynamicConfig, client.error, dynamicError]);

  // A client picker has no connection by definition, so the "connect first"
  // affordance must never be raised for one.
  if (clientDynamicConfig) {
    return {
      options,
      isLoading: client.isLoading,
      error,
      isAuthError: false,
      hasLoaded: client.hasLoaded,
      dependenciesSatisfied: client.dependenciesSatisfied,
      missingDependencies: client.missingDependencies,
      missingConnection: false,
      runtimeDependencies: [],
      refetch: client.refetch,
    };
  }

  return {
    options,
    isLoading,
    error,
    isAuthError: !!isAuthError,
    hasLoaded,
    dependenciesSatisfied,
    missingDependencies,
    missingConnection,
    runtimeDependencies,
    refetch,
  };
}

/**
 * Check if a parameter has dynamic values configuration
 */
export function hasDynamicValuesConfig(parameterInfo: ParameterInfo | undefined): boolean {
  return !!parameterInfo?.info?.dynamicValues?.operationId || !!parameterInfo?.info?.clientDynamicValues;
}
